import { InfoOutlined, PlusOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Button,
  Empty,
  Flex,
  FormInstance,
  InputNumber,
  Select,
  Spin,
  Table
} from 'antd'
import { FormListFieldData, TableColumnsType } from 'antd/lib'
import { useMemo, useState } from 'react'

import { useStocksServiceGetMedicineListEndpointKey } from 'src/api/queries'
import {
  CounterpartContractProductEnrichedDto,
  CreateInventoryEntryItemDto,
  GetContractResponse,
  GetMedicationDispenseInternalInventoryItemResponse,
  GetMedicineListResponse,
  StocksService,
  TemplateDto
} from 'src/api/requests'
import {
  EditableFormItem,
  requiredRules,
  SubmitButtons,
  Text
} from 'src/components'
import { TvaSelect } from 'src/features/medicines-register/components'
import { MeasurementUnitsSelect } from 'src/features/medicines-register/components/MeasurementUnitsSelect'
import { fixedDecimal, getTotalTvaPrice, getTvaRate } from 'src/features/stock'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

import { useStyles } from '../styles'

import { ConsumablesModal } from './ConsumablesModal'

type Props = {
  item?: GetContractResponse | TemplateDto['content']
  fields: FormListFieldData[]
  form?: FormInstance
  add: () => void
  remove: (index: number) => void
}

type Medicine = {
  tvaUnitRateName?: string
} & CreateInventoryEntryItemDto

const fieldsToReset = [
  'stockProductId',
  'barCode',
  'name',
  'producerName',
  'expirationDate',
  'quantity',
  'unitPrice',
  'group',
  'dose',
  'form',
  'measurementUnitId',
  'measurementUnitName',
  'tvaUnitRateName',
  'tvaUnitRateId',
  'tvaTotal',
  'delivered_quantity',
  'outstanding_quantity'
]

const generateFields = (medicines: { name?: string }[]) =>
  medicines
    .map((_, index: number) =>
      fieldsToReset.map((field) => ['medicine', index, field])
    )
    .flat()

export const ConsumablesTable = ({
  fields,
  item,
  form,
  add,
  remove
}: Props) => {
  const [editingIndex, setEditingIndex] = useState<number>()
  const [productIndex, setProductIndex] = useState<number>()
  const [isNewConsumable, setIsNewConsumable] = useState(false)
  const [search, setSearch] = useState('')

  const debouncedSearch = useDebounce(search)
  const { styles } = useStyles()
  const { t } = useLocales()

  const isTemplateItem = (
    item: GetContractResponse | TemplateDto['content'] | undefined
  ) => {
    return !!item && typeof item === 'object' && 'content' in item
  }

  const products = isTemplateItem(item)
    ? item?.content?.products
    : item?.products

  const productsIds =
    products
      ?.map(
        (
          product:
            | CounterpartContractProductEnrichedDto
            | TemplateDto['content']
        ) => product?.stockProduct?.id ?? product?.stockProductId
      )
      .filter((id: string) => typeof id === 'string') || []

  const {
    data: medicinesData,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage,
    hasNextPage: hasNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [useStocksServiceGetMedicineListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getMedicineListEndpoint(
        pageParam,
        100,
        debouncedSearch,
        undefined,
        undefined,
        undefined,
        undefined,
        [],
        productsIds
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetMedicineListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const combinedData = useMemo(() => {
    const medicines = medicinesData?.pages.flatMap((page) => page.data) || []

    const allItems = [
      ...medicines.map((item) => ({
        label: `${item?.commercialName} ${item?.doses} (${t('common.producer')}: ${item?.producer?.name} )`,
        value: item?.id,
        item
      }))
    ]

    const seen = new Set<string>()
    const uniqueByInternationalName = allItems?.filter(({ item }) => {
      const id = item?.internationalName?.id
      if (!id || seen.has(id)) return false
      seen.add(id)
      return true
    })

    return uniqueByInternationalName
  }, [medicinesData])

  const addConsumable = async () => {
    const medicines = form?.getFieldValue('medicine')

    if (medicines?.length > 0) {
      const fieldsToValidate = generateFields(medicines)

      await form?.validateFields(fieldsToValidate)
    }

    add()
    setEditingIndex(fields.length)
    setIsNewConsumable(true)
  }

  const onSelect = (index: number, value: string) => {
    const data = form?.getFieldValue('medicine')
    const selectedItem = combinedData.find((item) => item.value === value)

    const doses =
      selectedItem?.item && 'doses' in selectedItem.item
        ? selectedItem.item.doses
        : ''

    if (selectedItem) {
      data[index] = {
        ...data[index],
        stockProductId: selectedItem?.value,
        name: `${selectedItem?.item?.commercialName} ${doses}`,
        producerName: selectedItem?.item?.producer?.name,
        group: selectedItem?.item?.group?.category
      }

      setSearch('')
      form?.setFieldValue('medicine', data)
    }
  }

  const onSave = async () => {
    const medicines = form?.getFieldValue('medicine')

    const fieldsToValidate = generateFields(medicines)

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      setIsNewConsumable(false)
      setEditingIndex(undefined)
      form?.setFieldsValue({ medicine: values?.medicine })
    }
  }

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else {
      form?.resetFields(fieldsToReset.map((field) => ['stock', index, field]))
    }

    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const onDetails = (index: number) => {
    setProductIndex(index)
  }

  const columns: TableColumnsType = [
    {
      key: 'name',
      width: '20%',
      title: t('common.commercial_naming'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'name']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <Select
              allowClear
              showSearch
              popupMatchSelectWidth={false}
              filterOption={false}
              onSearch={(value) => setSearch(value)}
              onClear={() => setSearch('')}
              loading={isFetchingMedicines}
              options={combinedData}
              onSelect={(value) => onSelect(index, value)}
              placeholder={t('common.commercial_naming')}
              onPopupScroll={(e) => {
                const target = e.target as HTMLElement
                if (
                  target.scrollTop + target.offsetHeight ===
                  target.scrollHeight
                ) {
                  hasNextMedicinesPage && fetchNextMedicinesPage()
                }
              }}
              notFoundContent={
                isFetchingMedicines ? (
                  <Flex justify="center">
                    <Spin spinning />
                  </Flex>
                ) : (
                  <Empty />
                )
              }
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'group',
      width: '10%',
      align: 'center',
      title: t('common.group'),
      render: (_, __, index) => {
        const group = form?.getFieldValue(['medicine', index, 'group'])

        return group ? t(`common.${group}`) : '-'
      }
    },
    {
      key: 'measurementUnitId',
      width: '10%',
      align: 'center',
      title: t('common.measure_unit'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'measurementUnitName']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <MeasurementUnitsSelect
              placeholder={t('common.measure_unit')}
              onChange={(value, option) => {
                if (!option || Array.isArray(option)) return

                form?.setFieldsValue({
                  medicine: form
                    .getFieldValue('medicine')
                    .map(
                      (
                        med: GetMedicationDispenseInternalInventoryItemResponse,
                        i: number
                      ) =>
                        i === editingIndex
                          ? {
                              ...med,
                              measurementUnitId: value,
                              measurementUnitName: option?.label
                            }
                          : med
                    )
                })
              }}
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'quantity',
      width: '10%',
      align: 'center',
      title: t('counterparts.quantity'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'quantity']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <InputNumber
              min={0}
              placeholder={t('common.quantity')}
              className="full-width"
            />
          </EditableFormItem>
        )
      }
    },
    ...(item
      ? [
          {
            key: 'delivered_quantity',
            width: '10%',
            align: 'center' as const,
            title: t('counterparts.delivered_quantity'),
            render: (_: unknown, __: unknown, index: number) => {
              return (
                <EditableFormItem
                  name={[index, 'delivered_quantity']}
                  isEditing={editingIndex === index}
                  rules={requiredRules}
                >
                  <InputNumber
                    min={0}
                    placeholder={t('common.delivered_quantity')}
                    className="full-width"
                  />
                </EditableFormItem>
              )
            }
          },
          {
            key: 'outstanding_quantity',
            width: '10%',
            align: 'center' as const,
            title: t('counterparts.outstanding_quantity'),
            render: (_: unknown, __: unknown, index: number) => {
              return (
                <EditableFormItem
                  name={[index, 'outstanding_quantity']}
                  isEditing={editingIndex === index}
                  rules={requiredRules}
                >
                  <InputNumber
                    min={0}
                    placeholder={t('common.outstanding_quantity')}
                    className="full-width"
                  />
                </EditableFormItem>
              )
            }
          }
        ]
      : []),
    {
      key: 'unitPrice',
      width: '10%',
      align: 'center',
      title: t('stock_administration.price_unit'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'unitPrice']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <InputNumber
              min={0}
              className="full-width"
              placeholder={t('stock_administration.price_unit')}
              onChange={(value) => {
                const medicines = form?.getFieldValue('medicine') || []

                const updatedMedicines = medicines.map(
                  (med: Medicine, i: number) => {
                    if (i !== editingIndex) return med

                    const tvaRate = getTvaRate(med?.tvaUnitRateName as string)
                    const unitPriceFixed = fixedDecimal(value ?? 0, 4)

                    return {
                      ...med,
                      unitPrice: unitPriceFixed,
                      tvaTotal:
                        value !== undefined && med?.tvaUnitRateName
                          ? Math.round((value ?? 0) * (1 + tvaRate) * 10000) /
                            10000
                          : 0
                    }
                  }
                )

                form?.setFieldsValue({ medicine: updatedMedicines })
              }}
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'tvaUnitRateId',
      width: '10%',
      align: 'center',
      title: t('common.tva'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'tvaUnitRateName']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <TvaSelect
              allowClear={false}
              onChange={(value, option) => {
                if (!option || Array.isArray(option)) return

                const medicines = form?.getFieldValue('medicine') || []

                const updatedMedicines = medicines.map(
                  (med: Medicine, i: number) => {
                    if (i !== editingIndex) return med

                    const tvaRate = getTvaRate(option?.label as string)

                    const unitPrice = med?.unitPrice ?? 0

                    return {
                      ...med,
                      tvaUnitRateId: value,
                      tvaUnitRateName: option?.label,
                      tvaTotal:
                        unitPrice !== undefined
                          ? Math.round(unitPrice * (1 + tvaRate) * 10000) /
                            10000
                          : undefined
                    }
                  }
                )

                form?.setFieldsValue({ medicine: updatedMedicines })
              }}
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'total_tva',
      width: '10%',
      title: t('common.total_tva'),
      align: 'center',
      render: (_, __, index) => {
        const quantity = form?.getFieldValue(['medicine', index, 'quantity'])
        const unitPrice = form?.getFieldValue(['medicine', index, 'unitPrice'])
        const tvaRate = form?.getFieldValue([
          'medicine',
          index,
          'tvaUnitRateName'
        ])

        return getTotalTvaPrice(quantity, unitPrice, tvaRate)
      }
    },
    {
      key: 'tvaUnitRateName',
      hidden: true
    },
    ...(!item || isTemplateItem(item)
      ? [
          {
            width: '10%',
            key: 'actions',
            fixed: 'right' as const,
            render: (_: unknown, __: unknown, index: number) => (
              <SubmitButtons
                editingIndex={editingIndex}
                isEditing={editingIndex === index}
                onSave={onSave}
                onCancel={() => onCancel(index)}
                onEdit={() => setEditingIndex(index)}
                onDelete={() => remove(index)}
                onDetails={() => onDetails(index)}
              />
            )
          }
        ]
      : [
          {
            width: '5%',
            key: 'actions',
            fixed: 'right' as const,
            align: 'center' as const,
            render: (_: unknown, __: unknown, index: number) => (
              <Button
                icon={<InfoOutlined />}
                onClick={() => onDetails(index)}
              />
            )
          }
        ])
  ]

  return (
    <>
      <Text size="xxl">{t('common.medicines')}</Text>
      <Table
        className={styles.table}
        id="scrollableTable"
        dataSource={fields}
        bordered
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={columns}
        footer={() =>
          !item && (
            <Flex justify="center">
              <Button onClick={addConsumable} icon={<PlusOutlined />}>
                {t('counterparts.add_product')}
              </Button>
            </Flex>
          )
        }
      />

      {productIndex !== undefined && (
        <ConsumablesModal
          tab="medicine"
          form={form}
          productIndex={productIndex}
          onCancel={() => setProductIndex(undefined)}
        />
      )}
    </>
  )
}
